import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';

import { AbstractDialogComponent } from '../../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { DistributionService } from '../../../../../Common/TargetingShared/services/distribution.service';
import { ScreenType } from './screen-type.enum';
import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { DropdownOptionModel } from '../../../../../Common/Forms/models/dropdown-option.model';
import { RadioTypesEnum } from '../../../../../Common/Forms/enums/radio-types.enum';

// IMPORTANT: don't change these unless you're changing the sizes in the css as well
// widths/heights include the borders
const deviceSizes: {[key in ScreenType]: {width: number; height: number}} = {
    [ScreenType.desktop]: {
        width: 658,
        height: 0 // based on screen size instead
    },
    [ScreenType.tabletPortrait]: {
        width: 690,
        height: 846
    },
    [ScreenType.tabletLandscape]: {
        width: 846,
        height: 652
    },
    [ScreenType.mobilePortrait]: {
        width: 413,
        height: 780
    },
    [ScreenType.mobileLandscape]: {
        width: 780,
        height: 395
    },
};

@Component({
    selector: 'agpr-device-preview-dialog',
    templateUrl: './device-preview-dialog.component.html',
    styleUrls: ['./device-preview-dialog.component.scss'],
    standalone: false
})
export class DevicePreviewDialogComponent extends AbstractDialogComponent implements OnInit {
    readonly screenTypes = ScreenType;
    readonly buttonTypes = ButtonTypesEnum;
    readonly radioTypesEnum = RadioTypesEnum;

    @Input() selectedScreenType: ScreenType;
    @Output() selectedScreenTypeChanged: EventEmitter<ScreenType> = new EventEmitter<ScreenType>();

    scaleOptions: DropdownOptionModel<number>[] = [
        {
            label: '100%',
            value: 1
        },
        {
            label: '75%',
            value: 0.75
        },
        {
            label: '50%',
            value: 0.5
        },
        {
            label: 'Discover.distribution.preview.scale.fit',
            value: 0
        }
    ];

    body: string;
    scale: DropdownOptionModel<number> = this.scaleOptions[0];

    actualScale = 1;
    scaledWidth: number;
    scaledHeight: number;

    constructor(
        public coreDialogService: CoreDialogService,
        public distributionService: DistributionService
    ) {
        super(coreDialogService, 'DevicePreview');
    }


    async ngOnInit() {
        this.body = (await this.distributionService.getDistributionHtml({isTestEmail: true, disableViewInBrowserLink: true})).bodyHtml;

        this.resize();
    }

    @HostListener('window:resize')
    resize(): void {
        let scaleRatio = this.scale.value;
        const deviceSize = deviceSizes[this.selectedScreenType];
        const available = this.getAvailableSpace();

        if (this.scale.value === 0) {
            const widthRatio = available.width / deviceSize.width;
            const heightRatio = available.height / deviceSize.height;

            scaleRatio = Math.min(
                1,
                widthRatio,
                !deviceSize.height ? 1 : heightRatio,
            );
        }

        this.actualScale = scaleRatio;
        this.scaledWidth = deviceSize.width * scaleRatio;
        if (!deviceSize.height) {
            this.scaledHeight = available.height * scaleRatio;
        } else {
            this.scaledHeight = deviceSize.height * scaleRatio;
        }
    }

    private getAvailableSpace(): {width: number; height: number} {
        return {
            width: document.body.clientWidth * 0.75 - 50, // 50 is roughly the dialog border + device margins
            height: document.body.clientHeight * 0.75 - 190 // 190 is roughly the header + footer + content header + device margins
        };
    }

    cancelClicked(): void {
        this.selectedScreenTypeChanged.emit(undefined);
        this.closeDialog();
    }

    changeSelectedScreenType(screenType: ScreenType) {
        this.selectedScreenType = screenType;
        this.selectedScreenTypeChanged.emit(screenType);
        if (screenType === ScreenType.desktop) {
            this.scale = this.scaleOptions.find(opt => opt.value === 1);
        }
        this.resize();
    }
}
